import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, Check, AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { formatCurrency } from "../utils/formatters";

// Historique Page - CA des années précédentes
export const HistoriquePage = () => {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statutTva, setStatutTva] = useState(null);
  const [formData, setFormData] = useState({ year: new Date().getFullYear() - 1, ca_principal: '', ca_secondaire: '', tva_nette: '' });
  const api = useApi();
  const { user } = useAuth();

  useEffect(() => { loadHistorique(); loadStatutTva(); }, []);

  const loadStatutTva = async () => {
    try {
      const res = await api.get("/dashboard");
      setStatutTva(res.data?.statut_tva || null);
    } catch (_) { /* silencieux */ }
  };

  const loadHistorique = async () => {
    try {
      const response = await api.get("/historique");
      setHistorique(response.data || []);
    } catch (err) { toast.error("Erreur de chargement"); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/historique", {
        year: parseInt(formData.year),
        ca_principal: parseFloat(formData.ca_principal) || 0,
        ca_secondaire: parseFloat(formData.ca_secondaire) || 0,
        // tva_nette uniquement si l'utilisateur a saisi quelque chose (sinon null)
        tva_nette: formData.tva_nette !== '' ? parseFloat(formData.tva_nette) : null,
      });
      toast.success("Historique enregistré");
      setShowForm(false);
      setFormData({ year: new Date().getFullYear() - 1, ca_principal: '', ca_secondaire: '', tva_nette: '' });
      loadHistorique();
    } catch (err) { toast.error("Erreur lors de l'enregistrement"); }
  };

  const handleDelete = async (year) => {
    if (window.confirm(`Supprimer l'historique ${year} ?`)) {
      try {
        await api.delete(`/historique/${year}`);
        toast.success("Historique supprimé");
        loadHistorique();
      } catch (err) { toast.error("Erreur"); }
    }
  };

  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let y = currentYear - 1; y >= currentYear - 5; y--) {
    if (!historique.find(h => h.year === y)) {
      availableYears.push(y);
    }
  }

  // Calcul TVA
  // Seuils TVA 2025-2026 pour le rappel
  const seuil_tva_base = user?.type_activite === "vente de marchandises" ? 85000 : 37500;

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="historique-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{color: '#210875'}}>Mon Historique</h1>
            <p className="text-muted-foreground">
              Mon chiffre d'affaires <strong>encaissé</strong> des années précédentes
            </p>
          </div>
          {availableYears.length > 0 && (
            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
              <Plus size={20} /> Ajouter une année
            </button>
          )}
        </div>

        <div className="p-4 rounded-lg" style={{backgroundColor: '#FDFCFF40'}}>
          <p className="text-sm" style={{color: '#210875'}}>
            <strong>💡 CA à renseigner :</strong> indique le CA <strong>encaissé</strong> sur l'année (pas le CA facturé).
            En régime micro, le CA est compté sur la date d'encaissement, conformément à la comptabilité de caisse (CGI art. 102 ter / 50-0).
          </p>
          <p className="text-sm mt-3" style={{color: '#210875'}}>
            <strong>Pourquoi renseigner mon historique ?</strong> Le CA des années précédentes est utilisé pour :
          </p>
          <ul className="text-sm mt-2 space-y-1" style={{color: '#210875'}}>
            <li>• Déterminer si vous passez au régime de TVA au 1<sup>er</sup> janvier</li>
            <li>• Calculer votre CFE (exonération si CA &lt; 5 000€)</li>
            <li>• Analyser l'évolution de votre activité</li>
          </ul>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 w-full max-w-md animate-slide-up">
              <h2 className="text-xl font-bold mb-4" style={{color: '#210875'}}>Ajouter une année</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Année</label>
                  <select value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background">
                    {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CA encaissé — Activité principale (€)</label>
                  <input type="number" value={formData.ca_principal} onChange={(e) => setFormData({ ...formData, ca_principal: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" min="0" step="1" placeholder="ex: 25000" required />
                </div>
                {user?.double_activite && (
                  <div>
                    <label className="block text-sm font-medium mb-2">CA encaissé — Activité secondaire (€)</label>
                    <input type="number" value={formData.ca_secondaire} onChange={(e) => setFormData({ ...formData, ca_secondaire: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" min="0" step="1" placeholder="ex: 5000" />
                  </div>
                )}
                {/* TVA nette N-1 — uniquement pour les assujettis qui ne sont PAS en 1ère année d'assujettissement.
                    En 1ère année, l'user était en franchise N-1 donc tva_nette N-1 = 0 par construction. */}
                {statutTva?.assujetti && !statutTva?.premiere_annee_assujettissement && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      TVA nette de l'année (€) <span className="text-xs text-muted-foreground font-normal">— optionnel</span>
                    </label>
                    <input
                      type="number"
                      value={formData.tva_nette}
                      onChange={(e) => setFormData({ ...formData, tva_nette: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-input bg-background"
                      min="0"
                      step="0.01"
                      placeholder="ex: 4 200 (TVA collectée − TVA déductible)"
                      data-testid="historique-tva-nette-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                      💡 Renseigne uniquement si tu étais <strong>déjà assujetti à la TVA</strong> cette année-là (CA12 ou CA3 déclarée).
                      Si tu étais en franchise (CA &lt; seuil) ou en zone de tolérance, <strong>laisse vide</strong>.
                    </p>
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-lg border border-input hover:bg-muted transition-colors">Annuler</button>
                  <button type="submit" className="flex-1 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-card rounded-xl border overflow-hidden">
          {historique.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Calendar className="mx-auto mb-4 opacity-50" size={48} />
              <p>Aucun historique enregistré</p>
              <p className="text-sm mt-2">Ajoutez votre CA des années précédentes pour une meilleure analyse</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Année</th>
                    <th className="text-right p-4 font-medium">CA encaissé principal</th>
                    {user?.double_activite && <th className="text-right p-4 font-medium">CA encaissé secondaire</th>}
                    <th className="text-right p-4 font-medium">CA encaissé total</th>
                    <th className="text-center p-4 font-medium">Seuil TVA</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {historique.sort((a, b) => b.year - a.year).map((h) => {
                    const total = (h.ca_principal || 0) + (h.ca_secondaire || 0);
                    const depasse_tva = total > seuil_tva_base;
                    return (
                      <tr key={h.year} className="hover:bg-muted/30 transition-colors">
                        <td className="p-4 font-bold" style={{color: '#210875'}}>{h.year}</td>
                        <td className="p-4 text-right font-mono" style={{color: '#F8BC91'}}>{formatCurrency(h.ca_principal)}</td>
                        {user?.double_activite && <td className="p-4 text-right font-mono" style={{color: '#897FCB'}}>{formatCurrency(h.ca_secondaire)}</td>}
                        <td className="p-4 text-right font-mono font-bold">{formatCurrency(total)}</td>
                        <td className="p-4 text-center">
                          {depasse_tva ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <AlertTriangle size={12} /> Dépassé
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              <Check size={12} /> OK
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <button onClick={() => handleDelete(h.year)} className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Légende TVA - seuils 2025-2026 */}
        <div className="bg-card rounded-xl border p-6">
          <h3 className="font-semibold mb-3" style={{color: '#897FCB'}}>Rappel seuils TVA (2025-2026)</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Prestation de services :</span>
              <span className="font-bold ml-2">{formatCurrency(37500)} (base) / {formatCurrency(41250)} (majoré)</span>
            </div>
            <div>
              <span className="text-muted-foreground">Vente de marchandises :</span>
              <span className="font-bold ml-2">{formatCurrency(85000)} (base) / {formatCurrency(93500)} (majoré)</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            ⚠️ Si vous dépassez le seuil majoré, vous êtes assujetti à la TVA immédiatement et définitivement (jusqu'à demande de retour en franchise après 2 ans sous le seuil de base).
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};
